<template lang="pug">
.recruitment-contact-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :busy="isLoading"
    sticky-header
    no-border-collapse
    table-class="basic-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中...
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません

    // セル：対応
    template(#cell(done)="row")
      SupportDoneSwitch(v-if="row.item.contactDatetime" v-model="row.item.done" @onSwitch="onChangeDone(row.item)")
    // セル：登録者
    template(#cell(contactType)="row")
      span {{ convertContactType(row.item.contactType) }}
    // セル：お問い合わせ日時
    template(#cell(contactDatetime)="row")
      span {{ convertDate(row.item.contactDatetime) }}
      span （
      span(:class="getWeekClass(convertWeek(row.item.contactDatetime))") {{ convertWeek(row.item.contactDatetime) }}
      span ）
      span {{ convertTime(row.item.contactDatetime) }}
    // セル：氏名／電話番号
    template(#cell(nameAndTel)="row")
      .name {{ row.item.name }}
      .tel {{ row.item.tel }}
    // セル：お問い合わせ内容
    template(#cell(contactMessage)="row")
      .overflow-hidden.title-text(:id="`contact-message-${row.item.created}`") {{ row.item.contactMessage }}
      b-popover(v-if="row.item.contactMessage" :target="`contact-message-${row.item.created}`" triggers="hover")
        .d-flex.flex-column
          span {{ `${row.item.contactMessage}` }}
    // セル：対応メモ
    template(#cell(supportMemo)="row")
      .overflow-hidden.title-text(:id="`support-memo-${row.item.created}`") {{ row.item.supportMemo }}
      b-popover(v-if="row.item.supportMemo" :target="`support-memo-${row.item.created}`" triggers="hover")
        .d-flex.flex-column
          div {{ `${row.item.supportMemo}` }}
    // セル：操作
    template(#cell(operation)="row")
      button.btn.edit-btn.mr-2(@click="onEdit(row.item)") 修正
      button.btn.delete-btn(@click="onDelete(row.item)" type="button") 削除
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { ContactType, RecruitmentContactsGetModel } from "@/commons/services/RecruitmentContacts";

import SupportDoneSwitch from "@/pc/basics/SupportDoneSwitch.vue";

/**
 * ### 施設見学テーブルコンポーネント
 */
@Component({
  components: {
    SupportDoneSwitch
  }
})
export default class RecruitmentContactTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<RecruitmentContactTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<RecruitmentContactTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isLoading!: boolean;
  /** フィルター */
  @Prop()
  filter!: FilterSetting;

  get _value(): Array<RecruitmentContactTableItem> {
    return this.value;
  }

  /**
   * ### 日時を「yyyy年mm月dd日」形式に変換する
   */
  public convertDate(datetime: number): string {
    const date: string = this.$dateUtil.convertNumberToString(datetime, "mm月dd日");
    return date;
  }

  /**
   * ### 日時から曜日文字列に変換する
   */
  public convertWeek(datetime: number): string {
    const week: string = this.$dateUtil.getWeekName(this.$dateUtil.convertNumberToDate(datetime), "ja");
    return week;
  }

  /**
   * ### 曜日に応じたCSSクラスを取得する
   */
  public getWeekClass(week: string): string {
    switch (week) {
      case "日": {
        return "sunday";
      }
      case "土": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 日時を「hh：MM」形式に変換する
   */
  public convertTime(datetime: number): string {
    const time: string = this.$dateUtil.convertNumberToString(datetime, "hh：MM");
    return time;
  }

  /**
   * ### 申込み種別を文字列に変換する
   */
  public convertContactType(applicationType: ContactType): string {
    if (!applicationType) {
      return "";
    } else {
      switch (applicationType) {
        case ContactType.USER: {
          return "本人";
        }
        case ContactType.FACILITY: {
          return "代理";
        }
        default: {
          return "";
        }
      }
    }
  }

  /**
   * ### フィルタリング
   */
  public filterTable(row: RecruitmentContactTableItem, filter: FilterSetting): boolean {
    return this.filterSearchWords(row, filter);
  }

  /**
   * ### 検索文字列によるフィルタリング
   */
  public filterSearchWords(row: RecruitmentContactTableItem, filter: FilterSetting): boolean {
    if (filter.searchWord == "") {
      return true;
    }

    const words: string[] = this.$filterUtil.createSearchWord(filter.searchWord);

    let foundName: boolean = true;
    let foundContactMessage: boolean = true;
    let foundSupportMemo: boolean = true;

    for (const word of words) {
      if (row.name.indexOf(word) == -1) {
        foundName = false;
      }

      if (row.contactMessage == null) {
        foundContactMessage = false;
      }
      if (row.contactMessage != null && row.contactMessage.indexOf(word) == -1) {
        foundContactMessage = false;
      }

      if (row.supportMemo == null) {
        foundSupportMemo = false;
      }
      if (row.supportMemo != null && row.supportMemo.indexOf(word) == -1) {
        foundSupportMemo = false;
      }
    }

    return foundName || foundContactMessage || foundSupportMemo;
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 対応状態スイッチの押下イベント
   */
  public onChangeDone(item: RecruitmentContactTableItem): void {
    this.$emit("onChangeDone", item);
    return;
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(item: RecruitmentContactTableItem): void {
    this.$emit("onEdit", item);
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: RecruitmentContactTableItem): void {
    this.$emit("onDelete", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface RecruitmentContactTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface RecruitmentContactTableItem extends RecruitmentContactsGetModel {}

export interface FilterSetting {
  searchWord: string;
}
</script>

<style lang="scss" scoped>
.recruitment-contact-table-component {
  .no-datetime {
    font-size: 14px;
  }

  // 文字がtd要素の幅をはみ出した場合「…」で隠すスタイル
  .overflow-hidden {
    overflow: hidden !important;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .edit-btn,
  .delete-btn {
    width: 64px;
  }
}
</style>
